import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Squares2X2Icon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ArchiveBoxIcon,
  PhotoIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  BanknotesIcon,
  CurrencyEuroIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  RectangleStackIcon,
  GlobeAltIcon,
  GiftIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";

const navigation = [
  { name: "AWIN Programs Manager", href: "/programs", icon: Squares2X2Icon },
  { name: "AWIN Programs Archives", href: "/archives", icon: ArchiveBoxIcon },
  { name: "AWIN Programs Sync", href: "/sync", icon: ArrowPathIcon },
  { name: "Slider Manager", href: "/sliders", icon: PhotoIcon },
  { name: "Flyer Templates", href: "/flyer-templates", icon: RectangleStackIcon },
  { name: "Countries", href: "/countries", icon: GlobeAltIcon },
  { name: "Referrals", href: "/referrals", icon: GiftIcon },
  { name: "Dealers", href: "/dealers", icon: BuildingStorefrontIcon },
  {
    name: "Bulk Dealer Check",
    href: "/dealers/bulk-check",
    icon: UserGroupIcon,
  },
  { name: "Customers", href: "/customers", icon: UserGroupIcon },
  { name: "Payouts", href: "/payouts", icon: BanknotesIcon },
  { name: "Commission Payouts", href: "/commissions", icon: CurrencyEuroIcon },
  { name: "Monthly Reports", href: "/monthly-reports", icon: DocumentChartBarIcon },
  { name: "Energy Savings", href: "/energy-savings", icon: BoltIcon },
  {
    name: "Consultant Requests",
    href: "/consultant-requests",
    icon: ChatBubbleLeftRightIcon,
  },
  { name: "Amazon", href: "/amazon-claims", icon: DocumentTextIcon },
  { name: "Settings", href: "/settings", icon: Cog6ToothIcon },
  {
    name: "Backoffice UUID Backfill",
    href: "/admin/backfill",
    icon: WrenchScrewdriverIcon,
  },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation();
  const { logout } = useAuth();

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:z-30 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ top: "64px", height: "calc(100vh - 64px)" }}
      >
        <div className="flex flex-col h-full">
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-r-2 border-primary-600"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                  }`}
                  onClick={onClose}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 ${
                      isActive
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Footer - Sign Out Button */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={logout}
              className="w-full flex items-center justify-center bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
